import { Sparkles } from 'lucide-react';
import { STUDIO_ACCENTS } from '../studioModel';
import { useProfileEditor } from '../state/profileEditorStore';
import type { AvatarFrame } from '../model/profileDoc';
import { CheckRow, ColorField, Hint, InspectorSection, Row, Seg } from './controls';
import { cn } from '@/lib/utils';

const FRAMES: { id: AvatarFrame; label: string }[] = [
  { id: 'none', label: 'None' },
  { id: 'accent', label: 'Accent' },
  { id: 'glow', label: 'Glow' },
  { id: 'gradient', label: 'Gradient' },
];

/** Identity properties for Profile Studio — every control maps 1:1 onto the
 *  real profile surfaces (hero card, chat popup, DM rail). */
export function ProfileInspector() {
  const doc = useProfileEditor((s) => s.doc);
  const patch = useProfileEditor((s) => s.patch);

  return (
    <>
      <InspectorSection title="Accent">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            title="Default (emblem glow)"
            onClick={() => patch((d) => ({ ...d, accent: null }))}
            className={cn(
              'grid h-6 w-6 place-items-center rounded-[5px] border border-dashed text-white/50 transition-colors',
              doc.accent == null ? 'border-accent text-accent' : 'border-white/25 hover:border-white/50',
            )}
          >
            <Sparkles size={11} />
          </button>
          {STUDIO_ACCENTS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Accent ${c}`}
              onClick={() => patch((d) => ({ ...d, accent: c }))}
              className={cn(
                'h-6 w-6 rounded-[5px] ring-1 ring-inset ring-white/10 transition-transform hover:scale-105',
                doc.accent === c && 'ring-2 ring-white',
              )}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        {doc.accent != null && (
          <Row label="Custom">
            <ColorField value={doc.accent} onChange={(accent) => patch((d) => ({ ...d, accent }), 'accent')} />
          </Row>
        )}
        <Hint>Default follows your emblem's glow color — pick a swatch to override it everywhere.</Hint>
      </InspectorSection>

      <InspectorSection title="Avatar frame">
        <Seg value={doc.frame} options={FRAMES} onChange={(frame) => patch((d) => ({ ...d, frame }))} />
      </InspectorSection>

      <InspectorSection title="Name & badges">
        <Row label="Name style">
          <Seg
            value={doc.nameStyle}
            options={[
              { id: 'plain', label: 'Plain' },
              { id: 'gradient', label: 'Gradient' },
            ]}
            onChange={(nameStyle) => patch((d) => ({ ...d, nameStyle }))}
          />
        </Row>
        <CheckRow
          label="Show emblem"
          checked={doc.showEmblem}
          onChange={(showEmblem) => patch((d) => ({ ...d, showEmblem }))}
        />
      </InspectorSection>

      <InspectorSection title="Banner" defaultOpen={false}>
        <Hint>
          Design your banner in the Banner tab — a layered canvas with images, text, shapes,
          gradients and glass. It replaces the title-card art on every profile surface.
        </Hint>
      </InspectorSection>
    </>
  );
}
